import type { AccessGroupItem, InviteTemplateGrant } from "@/features/identity-hub/api/identity-hub.api";
import { isPerTenantAssignable } from "@/features/identity-hub/lib/access-scope";
import { isGrantablePermissionSet, type PermissionTuple } from "@/features/identity-hub/lib/grantable-role";

/** Prefix for a catalog role that has no live group on the target org yet. */
export const TEMPLATE_ROLE_OPTION_PREFIX = "template:";

export type { InviteTemplateGrant };

export type InviteWildcardTemplate = {
  id: string;
  name: string;
  scopeMode: "FIXED" | "WILDCARD";
  status: "ACTIVE" | "ARCHIVED";
};

type LiveTemplateRef = {
  name?: string | null;
  templateId?: string | null;
  template?: { id?: string | null } | null;
  deletedAt?: string | null;
};

export function templateRoleOptionId(templateId: string): string {
  return `${TEMPLATE_ROLE_OPTION_PREFIX}${templateId}`;
}

export function parseTemplateRoleOptionId(roleId: string): string | null {
  if (!roleId.startsWith(TEMPLATE_ROLE_OPTION_PREFIX)) return null;
  const templateId = roleId.slice(TEMPLATE_ROLE_OPTION_PREFIX.length);
  return templateId.length > 0 ? templateId : null;
}

/** Live access id from a completed catalog-grant status, for the org just granted. */
export function accessIdFromTemplateGrantStatus(
  status: Record<string, unknown>,
  tenantId: string,
): string | null {
  const results = status.results;
  if (!Array.isArray(results)) return null;
  for (const row of results) {
    if (!row || typeof row !== "object") continue;
    const record = row as { tenantId?: unknown; accessId?: unknown };
    if (record.tenantId === tenantId && typeof record.accessId === "string" && record.accessId.length > 0) {
      return record.accessId;
    }
  }
  return null;
}

function liveTemplateId(group: LiveTemplateRef): string | null {
  return group.template?.id ?? group.templateId ?? null;
}

/**
 * Active WILDCARD catalog roles that are not already a live group on this org.
 * Wildcard create does not project the role onto every organization, so the
 * invite dropdown has to offer the catalog row itself.
 * A live role with the same name is one row already — offering the catalog
 * copy would be a second choice the grant rejects as a name conflict.
 */
export function unprojectedWildcardOptions(
  liveGroupsOnOrg: LiveTemplateRef[],
  templates: InviteWildcardTemplate[],
): Array<{ id: string; name: string; templateId: string }> {
  const represented = new Set<string>();
  const takenNames = new Set<string>();
  for (const group of liveGroupsOnOrg) {
    if (group.deletedAt) continue;
    const templateId = liveTemplateId(group);
    if (templateId) represented.add(templateId);
    if (group.name) takenNames.add(group.name);
  }
  return templates
    .filter((template) =>
      template.scopeMode === "WILDCARD"
      && template.status === "ACTIVE"
      && !represented.has(template.id)
      && !takenNames.has(template.name))
    .map((template) => ({
      id: templateRoleOptionId(template.id),
      name: template.name,
      templateId: template.id,
    }));
}

export function inviteRoleOptionsForOrg(
  accessGroups: AccessGroupItem[],
  orgId: string,
  grantableKeys: ReadonlySet<string> | null,
  wildcardTemplates: InviteWildcardTemplate[],
): Array<{ id: string; name: string }> {
  const liveOnOrg = accessGroups.filter((group) => group.tenantId === orgId);
  const live = liveOnOrg
    .filter(
      (group) =>
        !group.deletedAt
        && isPerTenantAssignable(group)
        && isGrantablePermissionSet(group.permissions as PermissionTuple[] | undefined, grantableKeys),
    )
    .map((group) => ({ id: group.id, name: group.name }));
  return [...live, ...unprojectedWildcardOptions(liveOnOrg, wildcardTemplates)];
}

export function orgHasInviteRole(
  accessGroups: AccessGroupItem[],
  orgId: string,
  grantableKeys: ReadonlySet<string> | null,
  wildcardTemplates: InviteWildcardTemplate[],
): boolean {
  return inviteRoleOptionsForOrg(accessGroups, orgId, grantableKeys, wildcardTemplates).length > 0;
}

/** Split a per-org picker value into live access ids and catalog grants. */
export function partitionInviteSelections(
  selections: Array<{ orgId: string; accessId: string }>,
): { accessIds: string[]; templateGrants: InviteTemplateGrant[] } {
  const accessIds: string[] = [];
  const templateGrants: InviteTemplateGrant[] = [];
  for (const selection of selections) {
    if (!selection.accessId) continue;
    const templateId = parseTemplateRoleOptionId(selection.accessId);
    if (templateId) {
      templateGrants.push({ templateId, tenantId: selection.orgId });
    } else {
      accessIds.push(selection.accessId);
    }
  }
  return { accessIds, templateGrants };
}
